import { CartUpdatedEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/utilities';

let previousCartGiftTotal = null;
let pendingCartGiftPreviousTotal = null;
const dismissedGiftTierThresholds = new Set();

class CartGiftRewards extends HTMLElement {
  #activeTierId = null;
  #dialogRoot = null;
  #validateHandler = null;
  #closeHandler = null;
  #dialogClickHandler = null;
  #escapeHandler = null;
  #cartOpenHandler = null;

  connectedCallback() {
    this.#mountOverlay();
    this.#bindEvents();

    if (previousCartGiftTotal == null) {
      previousCartGiftTotal = this.#getCartTotal();
    }
  }

  disconnectedCallback() {
    this.#unbindEvents();
  }

  refresh() {
    this.#mountOverlay();
    this.#bindDialogEvents();
  }

  openTierByThreshold(threshold) {
    const tier = this.#getTiers().find((entry) => entry.threshold === threshold);
    if (!tier) return false;

    this.#ensureDrawerOpen();
    this.#openTier(tier.id);
    return true;
  }

  handleCartOpenAutoOpen() {
    if (!this.#isAutoOpenEnabled()) return;
    if (this.#isDialogOpen()) return;

    const tier = this.#getFirstUnclaimedUnlockedTier();
    if (!tier || dismissedGiftTierThresholds.has(tier.threshold)) return;

    this.#openTier(tier.id);
  }

  handleCartTotalChanged(previousTotal) {
    this.refresh();

    const newTotal = this.#getCartTotal();
    const prevTotal = previousTotal ?? previousCartGiftTotal ?? newTotal;
    previousCartGiftTotal = newTotal;

    if (!this.#isAutoOpenEnabled()) return;
    if (this.#isDialogOpen()) return;

    const newlyUnlockedTier = this.#getNewlyUnlockedTier(prevTotal, newTotal);
    if (!newlyUnlockedTier) return;

    this.#ensureDrawerOpen();
    this.#openTier(newlyUnlockedTier.id);
  }

  #isAutoOpenEnabled() {
    return this.dataset.autoOpen === 'true';
  }

  #isPreselectEnabled() {
    return this.dataset.preselectProduct === 'true';
  }

  #preselectProducts() {
    if (!this.#isPreselectEnabled()) return;

    const dialogBody = this.#queryDialog('[data-ref="gift-dialog-body"]');
    if (!dialogBody) return;

    const inputs = Array.from(dialogBody.querySelectorAll('[data-ref="gift-select"]:not(:disabled)'));
    if (inputs.length === 0) return;

    if (this.#isMultiSelectMode()) {
      for (const input of inputs) {
        input.checked = true;
        input.closest('[data-ref="gift-product"]')?.classList.add('is-selected');
      }
    } else {
      const firstInput = inputs[0];
      firstInput.checked = true;
      firstInput.closest('[data-ref="gift-product"]')?.classList.add('is-selected');
    }

    this.#updateValidateButtonState();
  }

  #ensureDrawerOpen() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && !drawer.classList.contains('is-open')) {
      drawer.open();
    }
  }

  #getOverlay() {
    if (this.#dialogRoot?.isConnected) {
      return this.#dialogRoot;
    }

    const drawerInner = this.closest('cart-drawer')?.querySelector('.cart-drawer__inner');
    const overlayInDrawer = drawerInner?.querySelector('[data-ref="gift-dialog"]');
    if (overlayInDrawer) {
      this.#dialogRoot = overlayInDrawer;
      return overlayInDrawer;
    }

    const overlayInComponent = this.querySelector('[data-ref="gift-dialog"]');
    if (overlayInComponent) {
      this.#dialogRoot = overlayInComponent;
    }

    return overlayInComponent;
  }

  #mountOverlay() {
    const drawerInner = this.closest('cart-drawer')?.querySelector('.cart-drawer__inner');
    const overlay = this.querySelector('[data-ref="gift-dialog"]');

    if (!drawerInner || !overlay) return;

    if (overlay.parentElement !== drawerInner) {
      drawerInner.appendChild(overlay);
    }

    this.#dialogRoot = overlay;
    this.#bindDialogEvents();
  }

  #queryDialog(selector) {
    return this.#getOverlay()?.querySelector(selector) ?? null;
  }

  #bindEvents() {
    this.#validateHandler = this.#onValidate.bind(this);
    this.#closeHandler = this.#closeDialog.bind(this);
    this.#dialogClickHandler = this.#onDialogBackdropClick.bind(this);
    this.#escapeHandler = this.#onEscapeKey.bind(this);
    this.#cartOpenHandler = this.#onCartOpen.bind(this);

    document.addEventListener('keydown', this.#escapeHandler);
    document.addEventListener(ThemeEvents.cartOpen, this.#cartOpenHandler);
    this.#bindDialogEvents();
  }

  #bindDialogEvents() {
    this.#queryDialog('[data-ref="gift-validate"]')?.removeEventListener('click', this.#validateHandler);
    this.#queryDialog('[data-ref="gift-dialog-close"]')?.removeEventListener('click', this.#closeHandler);
    this.#getOverlay()?.removeEventListener('click', this.#dialogClickHandler);

    this.#queryDialog('[data-ref="gift-validate"]')?.addEventListener('click', this.#validateHandler);
    this.#queryDialog('[data-ref="gift-dialog-close"]')?.addEventListener('click', this.#closeHandler);
    this.#getOverlay()?.addEventListener('click', this.#dialogClickHandler);
  }

  #unbindEvents() {
    this.#queryDialog('[data-ref="gift-validate"]')?.removeEventListener('click', this.#validateHandler);
    this.#queryDialog('[data-ref="gift-dialog-close"]')?.removeEventListener('click', this.#closeHandler);
    this.#getOverlay()?.removeEventListener('click', this.#dialogClickHandler);
    document.removeEventListener('keydown', this.#escapeHandler);
    document.removeEventListener(ThemeEvents.cartOpen, this.#cartOpenHandler);
  }

  #getCartSections() {
    const sections = [];
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer?.dataset.sectionId) {
      sections.push(cartDrawer.dataset.sectionId);
    }
    return sections;
  }

  #getCartTotal() {
    return parseInt(this.dataset.cartTotal, 10) || 0;
  }

  #getTiers() {
    return Array.from(this.querySelectorAll('[data-ref="cart-gift-tier"]'))
      .map((tierEl) => ({
        el: tierEl,
        id: tierEl.dataset.tierId,
        threshold: parseInt(tierEl.dataset.threshold, 10) || 0,
        claimed: tierEl.dataset.tierClaimed === 'true',
        products: tierEl.querySelector('[data-ref="tier-products"]'),
      }))
      .sort((tierA, tierB) => tierA.threshold - tierB.threshold);
  }

  #getFirstUnclaimedUnlockedTier() {
    const cartTotal = this.#getCartTotal();

    return this.#getTiers().find((tier) => cartTotal >= tier.threshold && !tier.claimed);
  }

  #getNewlyUnlockedTier(previousTotal, newTotal) {
    return this.#getTiers().find((tier) => {
      if (tier.claimed) return false;
      if (dismissedGiftTierThresholds.has(tier.threshold)) return false;
      if (newTotal < tier.threshold) return false;
      if (previousTotal >= tier.threshold) return false;
      return true;
    });
  }

  #getActiveTier() {
    if (!this.#activeTierId) return null;
    return this.#getTiers().find((entry) => entry.id === this.#activeTierId) ?? null;
  }

  #isMultiSelectMode() {
    return this.#getActiveTier()?.el?.dataset.allowMultiple === 'true';
  }

  #updateValidateButtonState() {
    const validateButton = this.#queryDialog('[data-ref="gift-validate"]');
    if (!validateButton) return;

    const hasSelection = !!this.#getOverlay()?.querySelector(
      '[data-ref="gift-dialog-body"] [data-ref="gift-select"]:checked',
    );

    validateButton.disabled = !hasSelection;
  }

  #isDialogOpen() {
    return this.#getOverlay()?.classList.contains('is-open') ?? false;
  }

  #openTier(tierId) {
    const tier = this.#getTiers().find((entry) => entry.id === tierId);
    if (!tier) return;

    this.#mountOverlay();
    this.#activeTierId = tierId;

    const dialogBody = this.#queryDialog('[data-ref="gift-dialog-body"]');
    const overlay = this.#getOverlay();
    if (!dialogBody || !overlay) return;

    dialogBody.replaceChildren();

    const productEls = tier.products?.querySelectorAll('[data-ref="gift-product"]') ?? [];

    if (productEls.length === 0) {
      const emptyMessage = document.createElement('p');
      emptyMessage.className = 'cart-gift-rewards__empty-message';
      emptyMessage.textContent = this.dataset.emptyMessage || 'Aucun produit cadeau configuré pour ce palier.';
      dialogBody.appendChild(emptyMessage);
    } else {
      productEls.forEach((productEl) => {
        const clone = productEl.cloneNode(true);

        clone.querySelectorAll('[data-ref="gift-option-select"]').forEach((select) => {
          select.addEventListener('change', this.#onVariantChange.bind(this));
        });

        const radio = clone.querySelector('[data-ref="gift-select"]');
        radio?.addEventListener('change', this.#onGiftSelect.bind(this));

        this.#syncProductVariant(clone);
        dialogBody.appendChild(clone);
      });
    }

    this.#resetValidationState();
    this.#preselectProducts();
    overlay.removeAttribute('hidden');
    overlay.classList.add('is-open');
  }

  #closeDialog(event) {
    event?.preventDefault();

    const overlay = this.#getOverlay();
    if (!overlay) return;

    overlay.classList.remove('is-open');
    overlay.hidden = true;
    this.#onDialogClosed();
  }

  #onDialogClosed() {
    const activeTier = this.#getActiveTier();

    if (activeTier) {
      dismissedGiftTierThresholds.add(activeTier.threshold);
    }

    this.#activeTierId = null;
    this.#resetValidationState();
  }

  #onDialogBackdropClick(event) {
    if (event.target.closest('.cart-gift-rewards__dialog-inner')) return;
    this.#closeDialog(event);
  }

  #onEscapeKey(event) {
    if (event.key !== 'Escape' || !this.#isDialogOpen()) return;
    this.#closeDialog(event);
  }

  #resetValidationState() {
    const validateButton = this.#queryDialog('[data-ref="gift-validate"]');
    if (validateButton) {
      validateButton.disabled = true;
    }

    this.#getOverlay()?.querySelectorAll('[data-ref="gift-dialog-body"] [data-ref="gift-product"]').forEach((productEl) => {
      productEl.classList.remove('is-selected');
      const selectInput = productEl.querySelector('[data-ref="gift-select"]');
      if (selectInput) selectInput.checked = false;
    });
  }

  #onGiftSelect(event) {
    const input = event.target;
    const productEl = input.closest('[data-ref="gift-product"]');

    if (this.#isMultiSelectMode()) {
      productEl?.classList.toggle('is-selected', input.checked);
    } else {
      this.#getOverlay()?.querySelectorAll('[data-ref="gift-dialog-body"] [data-ref="gift-product"]').forEach((entry) => {
        entry.classList.toggle('is-selected', entry === productEl);
      });
    }

    this.#updateValidateButtonState();
  }

  #onVariantChange(event) {
    const productEl = event.target.closest('[data-ref="gift-product"]');
    if (!productEl) return;
    this.#syncProductVariant(productEl);
  }

  #syncProductVariant(productEl) {
    const variantData = productEl.querySelector('[data-ref="gift-variant-data"]');
    const hiddenVariantInput = productEl.querySelector('[data-ref="gift-variant-id"]');
    const selectInput = productEl.querySelector('[data-ref="gift-select"]');
    const image = productEl.querySelector('.cart-gift-reward-product__image');
    const comparePriceEl = productEl.querySelector('[data-ref="gift-compare-price"]');

    if (hiddenVariantInput && selectInput) {
      selectInput.value = hiddenVariantInput.value;
      return;
    }

    if (!variantData || !selectInput) return;

    const selects = Array.from(productEl.querySelectorAll('[data-ref="gift-option-select"]'));
    const selectedValues = selects.map((select) => select.value);

    let matchedVariant = null;

    for (const option of variantData.options) {
      const variantOptions = (option.dataset.options || option.textContent || '').split(' / ').map((value) => value.trim());
      const isMatch = selectedValues.every((value, index) => variantOptions[index] === value);

      if (isMatch) {
        matchedVariant = option;
        break;
      }
    }

    if (!matchedVariant) {
      selectInput.value = '';
      selectInput.disabled = true;
      selectInput.checked = false;
      productEl.classList.remove('is-selected');
      this.#updateValidateButtonState();
      return;
    }

    selectInput.disabled = false;
    selectInput.value = matchedVariant.value;

    if (comparePriceEl && matchedVariant.dataset.price) {
      comparePriceEl.textContent = formatMoney(parseInt(matchedVariant.dataset.price, 10));
    }

    if (image && matchedVariant.dataset.image) {
      image.src = matchedVariant.dataset.image;
    }
  }

  async #onValidate(event) {
    event.preventDefault();

    if (!this.#activeTierId) return;

    const selectedInputs = Array.from(
      this.#getOverlay()?.querySelectorAll('[data-ref="gift-dialog-body"] [data-ref="gift-select"]:checked') ?? [],
    );

    if (selectedInputs.length === 0) return;

    const validateButton = this.#queryDialog('[data-ref="gift-validate"]');
    validateButton?.classList.add('is-loading');

    const activeTier = this.#getTiers().find((entry) => entry.id === this.#activeTierId);
    const sections = this.#getCartSections();

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: selectedInputs.map((input) => ({
            id: parseInt(input.value, 10),
            quantity: 1,
            properties: {
              _cart_gift: 'true',
              _cart_gift_tier: this.#activeTierId,
              _cart_gift_threshold: String(activeTier?.threshold ?? ''),
            },
          })),
          sections: sections.join(','),
        }),
      });

      const data = await response.json();

      if (data.status) {
        console.error('cart-gift-rewards:', data.message || data.description);
        return;
      }

      this.#closeDialog();

      if (activeTier?.threshold) {
        dismissedGiftTierThresholds.delete(activeTier.threshold);
      }

      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
      const newItemCount = currentItemCount != null ? currentItemCount + selectedInputs.length : undefined;

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'cart-gift-rewards',
            sections: data.sections,
          },
        ),
      );
    } catch (error) {
      console.error('cart-gift-rewards:', error);
    } finally {
      validateButton?.classList.remove('is-loading');
    }
  }

  #onCartOpen() {
    this.#mountOverlay();
    window.requestAnimationFrame(() => {
      this.handleCartOpenAutoOpen();
    });
  }
}

if (!customElements.get('cart-gift-rewards')) {
  customElements.define('cart-gift-rewards', CartGiftRewards);
}

function getPreviousCartGiftTotal(rewards) {
  if (pendingCartGiftPreviousTotal != null) {
    return pendingCartGiftPreviousTotal;
  }

  if (previousCartGiftTotal != null) {
    return previousCartGiftTotal;
  }

  return rewards ? parseInt(rewards.dataset.cartTotal, 10) || 0 : 0;
}

document.addEventListener(
  ThemeEvents.cartUpdated,
  () => {
    const oldRewards = document.querySelector('cart-drawer cart-gift-rewards');

    pendingCartGiftPreviousTotal = oldRewards
      ? parseInt(oldRewards.dataset.cartTotal, 10) || 0
      : (previousCartGiftTotal ?? 0);
  },
  { capture: true },
);

document.addEventListener(ThemeEvents.cartUpdated, () => {
  window.requestAnimationFrame(() => {
    const rewards = document.querySelector('cart-drawer cart-gift-rewards');
    if (!rewards) return;

    const previousTotal = getPreviousCartGiftTotal(rewards);
    pendingCartGiftPreviousTotal = null;

    rewards.handleCartTotalChanged(previousTotal);
  });
});

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-ref="gift-choose-link"]');
  if (!link) return;

  const rewards = link.closest('cart-drawer')?.querySelector('cart-gift-rewards');
  if (!rewards) return;

  event.preventDefault();
  event.stopPropagation();

  const threshold = parseInt(link.dataset.giftThreshold, 10);
  if (!threshold) return;

  rewards.openTierByThreshold(threshold);
});
